import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getSubscription, isSubscriptionActive as checkSubscriptionActive } from '@traction/api-client';
import type { Subscription, PlanTier } from '@traction/shared';
import { PLAN_CONFIGS } from '@traction/shared';

interface SubscriptionContextType {
  subscription: Subscription | null;
  loading: boolean;
  isActive: boolean;
  tier: PlanTier;
  leadsPerDay: number;
  canAccessFeature: (feature: FeatureGate) => boolean;
  refreshSubscription: () => Promise<void>;
}

export type FeatureGate =
  | 'lead_generation'
  | 'email_drafting'
  | 'gmail_integration'
  | 'reply_detection'
  | 'priority_alerts'
  | 'csv_upload'
  | 'custom_integrations';

// Map features to minimum tier required
const FEATURE_TIERS: Record<FeatureGate, PlanTier[]> = {
  lead_generation: ['trial', 'starter', 'growth', 'scale'],
  email_drafting: ['trial', 'starter', 'growth', 'scale'],
  gmail_integration: ['trial', 'starter', 'growth', 'scale'],
  reply_detection: ['starter', 'growth', 'scale'],
  priority_alerts: ['growth', 'scale'],
  csv_upload: ['starter', 'growth', 'scale'],
  custom_integrations: ['scale'],
};

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [isActive, setIsActive] = useState(false);

  async function loadSubscription() {
    if (!user) {
      setSubscription(null);
      setIsActive(false);
      setLoading(false);
      return;
    }

    try {
      const sub = await getSubscription(user.uid);
      setSubscription(sub);

      if (sub) {
        const active = await checkSubscriptionActive(user.uid);
        setIsActive(active);
      } else {
        // No subscription = treat as trial for now
        setIsActive(true);
      }
    } catch (error) {
      console.error('Failed to load subscription:', error);
      // Default to trial behavior on error
      setIsActive(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSubscription();
  }, [user]);

  const tier: PlanTier = subscription?.plan_tier ?? 'trial';
  const leadsPerDay = PLAN_CONFIGS[tier]?.leads_per_day ?? 3;

  function canAccessFeature(feature: FeatureGate): boolean {
    if (!isActive) return false;
    const allowedTiers = FEATURE_TIERS[feature];
    return allowedTiers.includes(tier);
  }

  async function refreshSubscription() {
    await loadSubscription();
  }

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        isActive,
        tier,
        leadsPerDay,
        canAccessFeature,
        refreshSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
